import { formatDate } from '../../utils/date';
import { doPost } from '../http/httpHelper';
import { OrdersDataSource } from '../../datasources/orders/OrdersDataSource';
import { getDistributionCenterFromSapClient } from '../patients/patientsService';
import { queryProducts } from '../products/productService';
import { getProductsByCodes } from '../products/productService';
import { getSapSales } from '../sales/salesService';
import { listCompanies } from '../configuration/companiesService';
import { OrderEnums } from '../../shared/constants/OrderEnums';
import { UserHelper } from '../auth/UserHelper';
import { RechargeDataSource } from '../../datasources/recharge/RechargeDataSource';

const GASES_SEGMENT = '88';

const LATEST_SALES_CACHE_TTL_MS = 60 * 1000;
const latestSalesCache = new Map();
const latestSalesInFlight = new Map();

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return value;
    }
  }
  return '';
};

const normalizeSap = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  const withoutZeros = digits.replace(/^0+/, '');

  return withoutZeros || digits;
};

const isUsablePatientSap = (value) => normalizeSap(value).length >= 8;

const firstUsablePatientSap = (...values) => {
  for (const value of values) {
    const sap = normalizeSap(value);

    if (sap && isUsablePatientSap(sap)) {
      return sap;
    }
  }

  return '';
};

const getBallastPatientSap = (ballast) =>
  firstUsablePatientSap(
    ballast?.sap_customer,
    ballast?.cod_cli,
    ballast?.codCli,
    ballast?.BP,
    ballast?.receiving_code
  );

const getPatientSapClient = (patient) =>
  firstUsablePatientSap(
    patient?.sap_client,
    patient?.Sap_client,
    patient?.sap_number,
    patient?.sap,
    patient?.sap_customer,
    patient?.cod_cli,
    patient?.codCli,
    patient?.BP,
    patient?.receiving_code
  );

const resolveDivisaoByCnpj = async (cnpj, patient) => {
  const cnpjValue = String(cnpj ?? '').trim();
  if (!cnpjValue) return undefined;

  if (patient?.divisao !== undefined && patient?.divisao !== null) {
    return String(patient.divisao).trim();
  }

  try {
    const companies = await listCompanies();
    if (!Array.isArray(companies)) {
      return undefined;
    }

    const matchedCompany = companies.find(
      (company) => String(company?.cnpj ?? '').trim() === cnpjValue
    );

    if (matchedCompany) {
      if (
        matchedCompany?.divisao !== undefined &&
        matchedCompany?.divisao !== null
      ) {
        return String(matchedCompany.divisao).trim();
      }
    }
  } catch (error) {
    console.warn(
      '[saveRechargerOrder] Failed to resolve divisao by cnpj',
      error
    );
  }

  return undefined;
};

const getRechargeCnpjScope = () => {
  const currentUser = UserHelper.getCurrentUser();
  const isTruthy = (value) =>
    value === true || value === 1 || value === '1' || value === 'true';
  const isAdmin = isTruthy(currentUser?.isAdmin);
  const isWhiteMartins = isTruthy(currentUser?.isWhiteMartins);
  const canSeeAllCnpjs =
    isWhiteMartins &&
    (currentUser?.canSeeAllCnpjs === undefined ||
      currentUser?.canSeeAllCnpjs === null ||
      currentUser?.canSeeAllCnpjs === '' ||
      isTruthy(currentUser?.canSeeAllCnpjs));

  return isAdmin || canSeeAllCnpjs ? null : currentUser?.cnpj;
};

// O endpoint de recarga é a fonte de verdade para a seleção. Não consulte
// /getPatients por CPF: CPFs genéricos, como 00000000000, não identificam um
// paciente de forma única e podem retornar dados de outro cadastro.
export const searchRechargePatients = async (
  filter,
  page = 0,
  pageSize = 20
) => {
  const rechargeList = await new RechargeDataSource().getRecharge(
    page,
    pageSize,
    getRechargeCnpjScope(),
    filter
  );

  return {
    rows: Number(rechargeList?.rows ?? 0),
    data: (rechargeList?.data ?? []).map((ballast) => {
      const sap = getBallastPatientSap(ballast);
      const status = firstValue(
        ballast.patient_status,
        ballast.patitent_status,
        ballast.status
      );

      return {
        id: ballast.receiving_code ?? sap,
        name: firstValue(
          ballast.receiving_name,
          ballast.patient_social_reason,
          ballast.client_name
        ),
        cpf: ballast.cpf ?? '',
        cnpj: ballast.cnpj ?? '',
        status,
        patient_status: status,
        patitent_status: status,
        sap,
        sap_client: sap,
        sap_number: sap,
        receiving_code: ballast.receiving_code ?? '',
        ballast,
      };
    }),
  };
};

export const fetchPatientsWithCylinders = async (
  filter,
  onlyWithCylinders,
  page,
  pageSize
) => {
  let rechargeList = await new RechargeDataSource().getRecharge(
    page,
    pageSize,
    getRechargeCnpjScope(),
    filter
  );

  console.log('Fetched recharge list:', rechargeList);

  const patientSalesKeys = [];

  rechargeList.data.forEach((patient) => {
    const sapValue = getBallastPatientSap(patient);

    if (sapValue) {
      patientSalesKeys.push({ sap: sapValue });
    }
  });

  let sapSales = await Promise.all(
    patientSalesKeys.map(({ sap }) =>
      fetchLatestCylindersUsedByPatient(sap, GASES_SEGMENT)
    )
  );

  sapSales = sapSales.flat();

  rechargeList.data.forEach((patient, idx) => {
    const patientSap = getBallastPatientSap(patient);

    const matchedSalesGroup = sapSales.find((salesGroup) => {
      const salesSap = String(salesGroup?.sap ?? '').trim();
      return salesSap === patientSap;
    });

    rechargeList.data[idx].lastSales = matchedSalesGroup?.sales ?? [];
  });

  let prodIds = [];
  if (Array.isArray(sapSales)) {
    prodIds = sapSales
      .map((x) => x.sales)
      .flat()
      .map((x) => x.cod_prod);
  }

  rechargeList.data.forEach((patient) => {
    (patient?.quotaProducts ?? []).forEach((quotaProduct) => {
      prodIds.push(
        quotaProduct?.mapped_product_code || quotaProduct?.product_id
      );
    });
  });

  prodIds = new Set(
    prodIds.filter((x) => x !== undefined && x !== '' && x !== null)
  );

  const prods = await getProductsByCodes(Array.from(prodIds));

  const mappedProd = prods.reduce((acc, prod) => {
    if (prod?.sap_code !== undefined && prod?.sap_code !== null) {
      acc[prod.sap_code] = prod;
    }
    return acc;
  }, {});

  const patients = rechargeList.data.map((ballast) => {
    const deliveryZipcode = ballast.delivery_zipcode;
    const patientSap = getBallastPatientSap(ballast);
    const cep =
      typeof deliveryZipcode === 'string'
        ? deliveryZipcode
        : typeof deliveryZipcode === 'object'
        ? firstValue(
            deliveryZipcode?.cep,
            deliveryZipcode?.zipcode,
            deliveryZipcode?.zipCode
          )
        : '';

    const cylinders = buildRechargeCylinders(ballast, mappedProd);
    const addressNumber = firstValue(
      ballast.delivery_number,
      ballast.delivery_address_number,
      ballast.deliveryAddressNumber,
      ballast.address_number,
      ballast.number
    );

    return {
      id: ballast.receiving_code,
      name: ballast.receiving_name,
      cpf: ballast.cpf ?? '',
      cnpj: ballast.cnpj ?? '',
      status: firstValue(
        ballast.patient_status,
        ballast.patitent_status,
        ballast.status
      ),
      patitent_status: firstValue(
        ballast.patitent_status,
        ballast.patient_status,
        ballast.status
      ),
      patient_status: firstValue(
        ballast.patient_status,
        ballast.patitent_status,
        ballast.status
      ),
      cep: cep ?? '',
      city: firstValue(
        ballast.delivery_city,
        deliveryZipcode?.city,
        deliveryZipcode?.localidade
      ),
      address: firstValue(
        ballast.delivery_address,
        deliveryZipcode?.address,
        deliveryZipcode?.logradouro
      ),
      addressNumber,
      number: addressNumber,
      delivery_number: addressNumber,
      addressLine2: firstValue(
        ballast.delivery_complement,
        ballast.delivery_address_complement,
        ballast.delivery_address_line2,
        ballast.deliveryAddressLine2,
        deliveryZipcode?.complement,
        deliveryZipcode?.complemento,
        ballast.addressLine2,
        ballast.complement
      ),
      delivery_complement: ballast.delivery_complement ?? '',
      neighborhood: firstValue(
        ballast.delivery_neighborhood,
        ballast.delivery_district,
        deliveryZipcode?.neighborhood,
        deliveryZipcode?.bairro,
        ballast.neighborhood,
        ballast.district
      ),
      delivery_neighborhood: ballast.delivery_neighborhood ?? '',
      uf: firstValue(
        ballast.delivery_uf,
        ballast.delivery_state,
        ballast.uf,
        ballast.state,
        deliveryZipcode?.uf,
        deliveryZipcode?.state
      ),
      delivery_uf: ballast.delivery_uf ?? '',
      company: firstValue(
        ballast.client_name,
        ballast.company_social_name,
        ballast.company_name
      ),
      sap_client: patientSap,
      sap_number: patientSap,
      receiving_code: ballast.receiving_code ?? '',
      cylinders: cylinders,
      ballast,
    };
  });

  if (onlyWithCylinders) {
    const filteredPatients = patients.filter((x) => x.cylinders.length > 0);
    return {
      rows: Math.max(Number(rechargeList.rows ?? 0), filteredPatients.length),
      data: filteredPatients,
    };
  }
  return {
    rows: Math.max(Number(rechargeList.rows ?? 0), patients.length),
    data: patients,
  };
};

const normalizeProductCode = (value) => String(value ?? '').trim();

const normalizeSize = (value) =>
  String(value ?? '')
    .replace(',', '.')
    .trim();

const hasQuota = (quotaProduct) => Number(quotaProduct?.total_balance ?? 0) > 0;

const buildSalesTooltip = (salesSuggestions = []) => {
  if (!Array.isArray(salesSuggestions) || salesSuggestions.length === 0) {
    return '';
  }

  const parseSaleDate = (sale) => {
    const date = sale?.dat_venda || sale?.dat_Venda || '';
    const parts = String(date).split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime();
    }
    const parsed = new Date(date).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const lines = salesSuggestions
    .slice()
    .sort((a, b) => parseSaleDate(b) - parseSaleDate(a))
    .map((sale) => {
      const date = sale?.dat_venda || sale?.dat_Venda || '-';
      const quantity = sale?.num_cil ?? sale?.quantity ?? '-';
      return `Data: ${date} | Quant: ${quantity}`;
    });

  return ['Últimas vendas:', ...lines].join('\n');
};

const normalizeSaleSuggestion = (sale) => ({
  ...sale,
  source: 'msap_sales',
});

const salesMatchQuota = (sale, quotaProduct, cylinderId) => {
  const saleCode = normalizeProductCode(sale?.cod_prod);
  const quotaMappedCode = normalizeProductCode(
    quotaProduct?.mapped_product_code
  );
  const normalizedCylinderId = normalizeProductCode(cylinderId);

  return Boolean(
    saleCode && [quotaMappedCode, normalizedCylinderId].includes(saleCode)
  );
};

const buildQuotaCylinder = (quotaProduct, salesSuggestions, mappedProd) => {
  const productCode =
    quotaProduct?.mapped_product_code || quotaProduct?.product_id;
  const catalogProduct =
    mappedProd?.[productCode] ?? mappedProd?.[quotaProduct?.product_id];
  const normalizedSales = salesSuggestions.map(normalizeSaleSuggestion);

  return {
    type: catalogProduct?.type ?? '',
    product:
      catalogProduct?.description ?? quotaProduct?.product_description ?? null,
    size: catalogProduct?.size ?? '',
    id: productCode,
    quota: {
      ...quotaProduct,
      mapped_product: catalogProduct ?? null,
      source: quotaProduct?.source || 'msap',
    },
    salesSuggestions: normalizedSales,
    selectionMode: 'quota',
    availableActions: ['recarga', 'troca'],
    tooltipText: buildSalesTooltip(normalizedSales),
  };
};

const groupSalesByProduct = (sales) => {
  const grouped = new Map();

  (sales ?? []).forEach((sale) => {
    const key = normalizeProductCode(sale?.cod_prod);
    if (!key) return;

    if (!grouped.has(key)) {
      grouped.set(key, []);
    }

    grouped.get(key).push(sale);
  });

  return Array.from(grouped.values());
};

const buildSaleCylinderGroup = (salesGroup, mappedProd) => {
  const firstSale = salesGroup?.[0];
  const catalogProduct = mappedProd?.[firstSale?.cod_prod];
  const normalizedSales = salesGroup.map(normalizeSaleSuggestion);

  return {
    type: catalogProduct?.type ?? firstSale?.type ?? '',
    product:
      catalogProduct?.description ??
      firstSale?.description ??
      firstSale?.cod_prod ??
      null,
    size: catalogProduct?.size ?? firstSale?.size ?? '',
    id: firstSale?.cod_prod,
    quota: null,
    salesSuggestions: normalizedSales,
    selectionMode: 'sale',
    availableActions: ['recarga', 'troca'],
    tooltipText: buildSalesTooltip(normalizedSales),
  };
};

const buildGenericExchangeCylinder = () => ({
  type: '',
  product: 'Atendimento sem dados prévios',
  size: '',
  id: '80000017',
  quota: null,
  salesSuggestions: [],
  selectionMode: 'exchange',
  warningText:
    'No momento, não identificamos dados para esta recarga. Por gentileza, prossiga com a criação do pedido para tratarmos o atendimento.',
  availableActions: ['troca'],
  tooltipText: '',
});

const buildRechargeCylinders = (ballast, mappedProd) => {
  const quotaProducts = Array.isArray(ballast?.quotaProducts)
    ? ballast.quotaProducts.filter(hasQuota)
    : [];
  const sales = Array.isArray(ballast?.lastSales) ? ballast.lastSales : [];

  if (quotaProducts.length > 0) {
    return quotaProducts
      .map((quotaProduct) => {
        const productCode =
          quotaProduct?.mapped_product_code || quotaProduct?.product_id;
        const catalogProduct =
          mappedProd?.[productCode] ?? mappedProd?.[quotaProduct?.product_id];
        const matchingSales = sales.filter((sale) =>
          salesMatchQuota(
            sale,
            { ...quotaProduct, mapped_product: catalogProduct },
            productCode
          )
        );
        return buildQuotaCylinder(
          { ...quotaProduct, mapped_product: catalogProduct },
          matchingSales,
          mappedProd
        );
      })
      .filter((x) => x.product !== null);
  }

  if (sales.length > 0) {
    return groupSalesByProduct(sales)
      .map((salesGroup) => buildSaleCylinderGroup(salesGroup, mappedProd))
      .filter((x) => x.product !== null);
  }

  return [buildGenericExchangeCylinder()];
};

export const fetchLatestCylindersUsedByPatient = async (sap, subcategory) => {
  const sapValue = String(sap ?? '').trim();

  const cacheKey = `${sapValue}|${subcategory ?? 'all'}`;

  const cached = latestSalesCache.get(cacheKey);

  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  const inFlight = latestSalesInFlight.get(cacheKey);

  if (inFlight) {
    return await inFlight;
  }

  const promise = (async () => {
    const sales = await getSapSales(sapValue);

    const normalizeSalesArray = (payload) => {
      const candidates = [
        payload?.data,
        payload?.body?.data,
        payload?.body,
        payload,
      ];

      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
        if (typeof candidate === 'string') {
          try {
            const parsed = JSON.parse(candidate);
            if (Array.isArray(parsed)) return parsed;
            if (Array.isArray(parsed?.data)) return parsed.data;
            if (Array.isArray(parsed?.body?.data)) return parsed.body.data;
          } catch {
            // continue
          }
        }
      }

      return [];
    };

    const salesData = normalizeSalesArray(sales);
    const subcategorySales =
      subcategory !== null
        ? salesData.filter((x) => {
            const saleSubcategory = String(
              x?.product_subcategory_id ?? ''
            ).trim();
            return (
              saleSubcategory === String(subcategory) || saleSubcategory === ''
            );
          })
        : salesData;
    const filteredSales =
      subcategory !== null &&
      subcategorySales.length === 0 &&
      salesData.length > 0
        ? salesData
        : subcategorySales;
    const value = {
      sap: sapValue,
      sales: filteredSales,
    };

    latestSalesCache.set(cacheKey, {
      expiresAt: Date.now() + LATEST_SALES_CACHE_TTL_MS,
      value,
    });

    return value;
  })();

  latestSalesInFlight.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    latestSalesInFlight.delete(cacheKey);
  }
  /**
       * "id": 143,
        "cnpj": "46392148002325",
        "cpf": "22865574890",
        "company_social_name": "COORDENADORIA REGIONAL DE SAUDE SUL",
        "patient_social_reason": "VICTORIA FELISDORO XAVIER",
        "cod_cli": "20020519",
        "cod_prod": "40000520",
        "description": "Cilindro O2 Medicinal - 6,2 m³",
        "size": "6.2",
        "dat_venda": "2023-03-19",
        "product_category_id": "2",
        "product_subcategory_id": "88",
        "product_segment_id": "6"
       */
};

export const getAllGasesProducts = async () => {
  const prods = await queryProducts(
    null,
    null,
    null,
    null,
    '88',
    null,
    null,
    null,
    null,
    0,
    10000
  );
  if (Array.isArray(prods)) return prods;
  if (Array.isArray(prods?.data)) return prods.data;
  if (Array.isArray(prods?.body)) return prods.body;
  if (Array.isArray(prods?.body?.data)) return prods.body.data;
  return [];
};

function consolidateProducts(products) {
  const map = new Map();

  for (const p of products || []) {
    const key = String(p?.product_code ?? '').trim();
    if (!key) continue;

    const qty = Number(p?.quantity_volume ?? 0);
    const safeQty = Number.isFinite(qty) ? qty : 0;
    if (safeQty <= 0) continue;

    if (!map.has(key)) {
      map.set(key, { ...p, product_code: key, quantity_volume: safeQty });
    } else {
      const prev = map.get(key);
      const prevQty = Number(prev?.quantity_volume ?? 0);
      map.set(key, {
        ...prev,
        quantity_volume: (Number.isFinite(prevQty) ? prevQty : 0) + safeQty,
      });
    }
  }

  return Array.from(map.values());
}

function extractOrderNumberFromResult(result, fallbackOrderNumber) {
  if (typeof result === 'string' || typeof result === 'number') {
    return String(result);
  }

  if (result && typeof result === 'object') {
    const candidates = [
      result.request_number_temp,
      result.requestNumberTemp,
      result.request_number,
      result.requestNumber,
      result.orderNumber,
      result.order_number,
      result?.data?.request_number_temp,
      result?.body?.request_number_temp,
    ];

    for (const c of candidates) {
      if (typeof c === 'string' || typeof c === 'number') return String(c);
    }

    // Backend sometimes returns a status object (dbSucess/sapSucess). In this case, show the temp number.
    if (
      'dbSucess' in result ||
      'sapSucess' in result ||
      'logSucess' in result
    ) {
      return String(fallbackOrderNumber);
    }
  }

  return String(fallbackOrderNumber);
}

// Função para salvar o pedido de recarga
export const saveRechargerOrder = async (
  currentUser,
  cnpj,
  patient,
  data,
  deliveryDate,
  techDetails,
  deliveryTurn
) => {
  const orderTempNumber = new Date().getTime().toString();

  const cpfToSend = patient.cpf;
  const cnpjToSend = cnpj;
  const divisaoToSend = await resolveDivisaoByCnpj(cnpjToSend, patient);

  let distribution_center = '';
  const sapCliente = getPatientSapClient(patient);

  try {
    if (sapCliente) {
      const dcResp = await getDistributionCenterFromSapClient(sapCliente);

      const dc = typeof dcResp === 'string' ? JSON.parse(dcResp) : dcResp;

      distribution_center = dc?.filial_id ? String(dc.filial_id) : '';

      if (!distribution_center) {
        console.warn(
          '[saveRechargerOrder] DISTRIBUTION CENTER vazio/ausente para SAP CLIENTE',
          sapCliente,
          dc
        );
      }
    } else {
      console.warn('[saveRechargerOrder] SAP CLIENTE is undefined or null');
    }
  } catch (e) {
    console.error(
      '[saveRechargerOrder] Error fetching distribution center:',
      e
    );
  }

  const turns = [];
  Object.keys(deliveryTurn)
    .filter((turn) => deliveryTurn[turn])
    .forEach((turn) => turns.push(turn));

  // ----------------- Recarga -----------------

  const rawRecharge = Object.values(data)
    .filter((x) => x.action === 'recarga' && x.quantity > 0)
    .map((x) => ({
      product_code: x.cylinder.id.toString(),
      product_unit_value: 0,
      quantity_volume: x.quantity > 0 ? x.quantity : 1,
    }));

  const toRecharge = consolidateProducts(rawRecharge);

  // ----------------- Troca -----------------
  const rawExchange = Object.values(data)
    .filter(
      (x) =>
        x.action === 'troca' && x.newCylinder !== undefined && x.quantity > 0
    )
    .map((x) => ({
      product_code: x.newCylinder.sap_code,
      product_unit_value: 0,
      quantity_volume: x.quantity,
    }));

  const toExchange = consolidateProducts(rawExchange);

  // ----------------- Coleta -----------------
  const rawCollect = Object.values(data)
    .filter(
      (x) =>
        x.action === 'troca' && x.newCylinder !== undefined && x.quantity > 0
    )
    .map((x) => ({
      product_code: x.cylinder.id.toString(),
      product_unit_value: 0,
      quantity_volume: x.quantity,
    }));

  const toCollect = consolidateProducts(rawCollect);

  const obj = {
    request_number_temp: orderTempNumber,
    commercialization_type: 'P',
    request_type: new OrderEnums().enums.types.RECHARGE,
    status_id: new OrderEnums().enums.status.UNDER_ANALYSIS,
    sap_client: sapCliente ?? '',
    Sap_client: sapCliente ?? '',
    cnpj: cnpjToSend,
    ...(divisaoToSend ? { divisao: divisaoToSend } : {}),
    cpf: cpfToSend,
    distribution_center: distribution_center,
    destination_sys_cod: '0',
    destination_system: 'SAP',
    date_entry: formatDate(new Date()),
    serial_note: '',
    net_total_value: 0,
    total_with_tax: 0,
    sta_date: formatDate(new Date()),
    create_in: new Date().toISOString(),
    create_by: currentUser.email,
    order_type_desc: new OrderEnums().enums.types.RECHARGE,
    delivery_forecast: formatDate(deliveryDate),
    delivery_window: turns.join('/'),
    num_carga: '0',
    obs: techDetails ?? '',
  };

  const objRecharge = {
    ...obj,
    products: toRecharge,
  };

  const objExchange = {
    ...obj,
    products: toExchange,
    exchange: true,
  };

  const objCollect = {
    ...obj,
    products: toCollect,
    hidden: true,
  };

  // Operador Responsável

  const currentUserJSON = UserHelper.getCurrentUser();
  if (currentUserJSON) {
    if (
      currentUserJSON.isWhiteMartins === '1' ||
      currentUserJSON.isAdmin === '1'
    ) {
      objRecharge.operator_cognito_id = currentUserJSON.username;
      objCollect.operator_cognito_id = currentUserJSON.username;
      objExchange.operator_cognito_id = currentUserJSON.username;
    }
  }

  const orderNumbers = [];

  const extractErrorMessage = (value) => {
    const pickFromObject = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      if (typeof obj.message === 'string' && obj.message.trim())
        return obj.message;
      if (typeof obj.sapMessage === 'string' && obj.sapMessage.trim())
        return obj.sapMessage;
      if (typeof obj.error === 'string' && obj.error.trim()) return obj.error;
      if (typeof obj.detail === 'string' && obj.detail.trim())
        return obj.detail;
      return null;
    };

    if (value == null) return 'Erro ao criar pedido';
    if (typeof value === 'string') return value;
    if (value instanceof Error && typeof value.message === 'string')
      return value.message;

    const direct = pickFromObject(value);
    if (direct) return direct;

    // Common wrapper: { httpStatus, body }
    const nested = pickFromObject(value?.body);
    if (nested) return nested;

    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  };

  const getHttpStatusFromError = (err) => {
    if (!err) return null;
    if (typeof err?.httpStatus === 'number') return err.httpStatus;
    if (typeof err?.status === 'number') return err.status;
    if (typeof err?.response?.status === 'number') return err.response.status;
    if (typeof err?.response?.statusCode === 'number')
      return err.response.statusCode;
    return null;
  };

  const shouldVerifyAfterError = (err) => {
    const httpStatus = getHttpStatusFromError(err);
    if (typeof httpStatus === 'number' && httpStatus >= 400 && httpStatus < 500)
      return false;
    return true;
  };

  const payload = [];

  if (objRecharge.products.length > 0) {
    orderNumbers.push(objRecharge.request_number_temp);
    payload.push(objRecharge);
  }
  if (objExchange.products.length > 0) {
    orderNumbers.push(objExchange.request_number_temp);
    payload.push(objExchange);
  }
  if (objCollect.products.length > 0) {
    orderNumbers.push(objCollect.request_number_temp);
    payload.push(objCollect);
  }

  //const resolvedProms = await Promise.all(proms);

  try {
    console.log('[saveRechargerOrder] Submitting requests data:', payload);

    const result = await doPost('/requests', payload);

    if (result?.httpStatus && result.httpStatus >= 400) {
      const msg = extractErrorMessage(result);
      const e = new Error(`[HTTP ${result.httpStatus}] ${msg}`);
      e.httpStatus = result.httpStatus;
      throw e;
    }

    const createdByMessage =
      result &&
      ((result.message && result.message.indexOf('Request created') >= 0) ||
        result === 'Request created');

    const createdByStatus =
      result && result.status && result.status >= 200 && result.status < 300;

    if (createdByMessage || createdByStatus) {
      return orderNumbers[0] ?? orderTempNumber;
    }

    // Backend can return an object (dbSucess/sapSucess/etc). In that case, return the order temp number.
    if (result && typeof result === 'object') {
      return extractOrderNumberFromResult(
        result,
        orderNumbers[0] ?? orderTempNumber
      );
    }

    const ordersDs = new OrdersDataSource();

    for (let i = 0; i < orderNumbers.length; i++) {
      const rn = orderNumbers[i];
      try {
        const verify = await ordersDs.getOrderProductDetails(rn);
        if (Array.isArray(verify) && verify.length > 0) {
          return rn;
        }
      } catch (err) {
        console.warn('[saveRechargerOrder] verify for', rn, 'failed', err);
      }
    }

    return extractOrderNumberFromResult(
      result,
      orderNumbers[0] ?? orderTempNumber
    );
  } catch (err) {
    console.error(
      '[saveRechargerOrder] POST /requests error, attempting verification by request_number_temp',
      err
    );

    if (!shouldVerifyAfterError(err)) {
      throw err;
    }

    try {
      const ordersDs = new OrdersDataSource();
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
        try {
          const v = await ordersDs.getOrderProductDetails(orderTempNumber);
          if (Array.isArray(v) && v.length > 0) {
            console.warn(
              '[saveRechargerOrder] Request found by verification after exception on attempt',
              i + 1
            );
            return orderNumbers[0];
          }
        } catch (err2) {
          console.warn(
            '[saveRechargerOrder] verify attempt failed after exception, retrying',
            err2
          );
        }
      }
    } catch (err3) {
      console.warn(
        '[saveRechargerOrder] unexpected error during verification retries',
        err3
      );
    }

    throw err;
  }
};
